/**
 * Reports API Data Source
 *
 * External API communication for the Reports feature
 */

import {Http} from '../../../Data/config/Http';
import {API_ROUTES} from '../../../constants/apiRoutes';
import {
  BaseApiResponse,
  MarketerDTO,
  MarketerStoresReportDTO,
  MarketerFinancialReportDTO,
  MarketerStockReportDTO,
  ExportResponseDTO,
  ReportFiltersDTO,
  ExportRequestDTO
} from '../models/report.dto';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';

export interface ReportsAPIDataSource {
  getMarketers(): Promise<BaseApiResponse<MarketerDTO[]>>;
  getMarketerStoresReport(marketerId: number, filters?: ReportFiltersDTO): Promise<BaseApiResponse<MarketerStoresReportDTO>>;
  getMarketerFinancialReport(marketerId: number, filters?: ReportFiltersDTO): Promise<BaseApiResponse<MarketerFinancialReportDTO>>;
  getMarketerStockReport(marketerId: number, filters?: ReportFiltersDTO): Promise<BaseApiResponse<MarketerStockReportDTO>>;
  exportMarketerReport(marketerId: number, request: ExportRequestDTO): Promise<BaseApiResponse<ExportResponseDTO | MarketerStoresReportDTO>>;
}

export class ReportsAPIDataSourceImpl implements ReportsAPIDataSource {
  constructor () {
    // Initialize with Http service
  }

  async getMarketers (): Promise<BaseApiResponse<MarketerDTO[]>> {
    try {
      // limit=all → fetch the complete marketer list for the selector.
      const response = await Http.get<BaseResponse<MarketerDTO[]>>(API_ROUTES.REPORTS.MARKETERS, {
        params: {
          page: 1,
          limit: 'all'
        }
      });
      unwrapResponse(response.data, 'حدث خطأ في جلب المسوقين');
      return response.data as BaseApiResponse<MarketerDTO[]>;
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  async getMarketerStoresReport (marketerId: number, filters?: ReportFiltersDTO): Promise<BaseApiResponse<MarketerStoresReportDTO>> {
    try {
      const queryParams: Record<string, string> = {};

      if (filters?.date_from) {queryParams.date_from = filters.date_from;}
      if (filters?.date_to) {queryParams.date_to = filters.date_to;}
      if (filters?.store_id) {queryParams.store_id = filters.store_id.toString();}
      if (filters?.product_category) {queryParams.product_category = filters.product_category;}
      if (filters?.payment_status) {queryParams.payment_status = filters.payment_status;}

      const url = API_ROUTES.REPORTS.MARKETER_STORES(marketerId);
      const response = await Http.get<BaseResponse<MarketerStoresReportDTO>>(url, {params: queryParams});
      unwrapResponse(response.data, 'حدث خطأ في جلب تقرير متاجر المسوق');
      return response.data as BaseApiResponse<MarketerStoresReportDTO>;
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  async getMarketerFinancialReport (marketerId: number, filters?: ReportFiltersDTO): Promise<BaseApiResponse<MarketerFinancialReportDTO>> {
    try {
      const queryParams: Record<string, string> = {};

      if (filters?.date_from) {queryParams.date_from = filters.date_from;}
      if (filters?.date_to) {queryParams.date_to = filters.date_to;}
      if (filters?.store_id) {queryParams.store_id = filters.store_id.toString();}
      if (filters?.product_category) {queryParams.product_category = filters.product_category;}
      if (filters?.payment_status) {queryParams.payment_status = filters.payment_status;}

      const url = API_ROUTES.REPORTS.MARKETER_FINANCIAL(marketerId);
      const response = await Http.get<BaseResponse<MarketerFinancialReportDTO>>(url, {params: queryParams});
      unwrapResponse(response.data, 'حدث خطأ في جلب التقرير المالي للمسوق');
      return response.data as BaseApiResponse<MarketerFinancialReportDTO>;
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  async getMarketerStockReport (marketerId: number, filters?: ReportFiltersDTO): Promise<BaseApiResponse<MarketerStockReportDTO>> {
    try {
      const queryParams: Record<string, string | number | boolean> = {};
      if (filters?.date_from) {queryParams.date_from = filters.date_from;}
      if (filters?.date_to) {queryParams.date_to = filters.date_to;}
      if (filters?.product_category) {queryParams.product_category = filters.product_category;}
      if (typeof filters?.only_with_stock !== 'undefined') {queryParams.only_with_stock = filters.only_with_stock;}

      const url = API_ROUTES.REPORTS.MARKETER_STOCK(marketerId);
      const response = await Http.get<BaseResponse<MarketerStockReportDTO>>(url, {params: queryParams});
      unwrapResponse(response.data, 'حدث خطأ في جلب تقرير المخزون للمسوق');
      return response.data as BaseApiResponse<MarketerStockReportDTO>;
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  async exportMarketerReport (marketerId: number, request: ExportRequestDTO): Promise<BaseApiResponse<ExportResponseDTO | MarketerStoresReportDTO>> {
    try {
      const queryParams: Record<string, string> = {};

      queryParams.format = request.format;
      if (request.date_from) {queryParams.date_from = request.date_from;}
      if (request.date_to) {queryParams.date_to = request.date_to;}
      if (request.store_id) {queryParams.store_id = request.store_id.toString();}
      if (request.product_category) {queryParams.product_category = request.product_category;}
      if (request.payment_status) {queryParams.payment_status = request.payment_status;}

      const url = API_ROUTES.REPORTS.MARKETER_EXPORT(marketerId);
      const response = await Http.get<BaseResponse<ExportResponseDTO | MarketerStoresReportDTO>>(url, {params: queryParams});
      unwrapResponse(response.data, 'حدث خطأ في تصدير تقرير المسوق');
      return response.data as BaseApiResponse<ExportResponseDTO | MarketerStoresReportDTO>;
    } catch (error) {
      handleDataSourceError(error);
    }
  }
}

